import React, { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import Editor from '@monaco-editor/react';
import { Eye, Code, RefreshCw, Maximize2, Minimize2 } from 'lucide-react';
import VisualEmailEditor from './VisualEmailEditor';

/**
 * RichTextEditor - HTML Email Editor with Visual Mode Toggle
 * 
 * A professional HTML email editor with two modes:
 * - Visual Mode (DEFAULT): User-friendly click-to-edit interface
 * - HTML Mode: Monaco code editor with syntax highlighting
 * 
 * Features:
 * - Toggle between Visual and HTML modes
 * - Split-screen: Code editor + Live preview (HTML mode)
 * - Visual editing interface (Visual mode)
 * - Two-way sync between modes
 * - Syntax highlighting for HTML
 * - Line numbers and code folding
 * - Full HTML editing capability
 * - No formatting loss
 */
const RichTextEditor = forwardRef(({ value, onChange, onImageUpload, onInsertVariable, onInsertLogo }, ref) => {
  const [html, setHtml] = useState(value || '');
  const [editorMode, setEditorMode] = useState('visual'); // 'visual' | 'html'
  const [viewMode, setViewMode] = useState('split'); // 'split', 'code', 'preview' (for HTML mode)
  const [isFullscreen, setIsFullscreen] = useState(false);
  const editorRef = useRef(null);
  const previewRef = useRef(null);

  // Update local state when value prop changes
  useEffect(() => {
    if (value !== html) {
      setHtml(value || '');
    }
  }, [value]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    getEditor: () => editorRef.current,
    focus: () => {
      if (editorMode === 'html' && editorRef.current) {
        editorRef.current.focus();
      }
    },
    getValue: () => html,
    setValue: (newValue) => {
      setHtml(newValue);
      if (onChange) {
        onChange(newValue);
      }
    }
  }));

  // Handle editor change (from Monaco)
  const handleEditorChange = (newValue) => {
    setHtml(newValue || '');
    if (onChange) {
      onChange(newValue || '');
    }
  };

  // Handle visual editor change
  const handleVisualEditorChange = (newValue) => {
    setHtml(newValue || '');
    if (onChange) {
      onChange(newValue || '');
    }
  };

  // Handle editor mount
  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    
    // Configure HTML language features
    monaco.languages.html.htmlDefaults.setOptions({
      format: {
        tabSize: 2,
        insertSpaces: true,
        wrapLineLength: 120,
        wrapAttributes: 'auto',
      },
      suggest: {
        html5: true,
      }
    });
  };

  // Insert variable at cursor position (for HTML mode)
  const insertVariable = (variable) => {
    const editor = editorRef.current;
    if (!editor) return;

    const position = editor.getPosition();
    const range = {
      startLineNumber: position.lineNumber,
      startColumn: position.column,
      endLineNumber: position.lineNumber,
      endColumn: position.column
    };

    editor.executeEdits('', [{
      range: range,
      text: variable,
      forceMoveMarkers: true
    }]);

    editor.focus();
  };

  // Insert logo (for HTML mode)
  const insertLogo = () => {
    const logoHtml = `<img src="https://www.ellasmgt.com/logo.png" alt="Ella's Place Logo" style="max-width: 200px; height: auto; display: block; margin: 0 auto;" />`;
    
    const editor = editorRef.current;
    if (!editor) return;

    const position = editor.getPosition();
    const range = {
      startLineNumber: position.lineNumber,
      startColumn: position.column,
      endLineNumber: position.lineNumber,
      endColumn: position.column
    };

    editor.executeEdits('', [{
      range: range,
      text: logoHtml,
      forceMoveMarkers: true
    }]);

    editor.focus();
  };

  // Trigger callbacks from parent
  useEffect(() => {
    if (onInsertVariable && editorMode === 'html') {
      window.insertVariableToEditor = insertVariable;
    }
    if (onInsertLogo && editorMode === 'html') {
      window.insertLogoToEditor = insertLogo;
    }
  }, [onInsertVariable, onInsertLogo, editorMode]);

  // Toggle fullscreen
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Refresh preview (for HTML mode)
  const refreshPreview = () => {
    if (previewRef.current) {
      previewRef.current.srcdoc = html;
    }
  };

  // Switch to Visual Mode
  const switchToVisualMode = () => {
    setEditorMode('visual');
  };

  // Switch to HTML Mode
  const switchToHtmlMode = () => {
    setEditorMode('html');
  };

  return (
    <div className={`rich-text-editor ${isFullscreen ? 'fixed inset-0 z-50 bg-white' : 'relative'}`}>
      {/* Mode Toggle */}
      <div className="flex items-center justify-between bg-gradient-to-r from-blue-600 to-purple-600 border border-blue-700 rounded-t-lg px-4 py-3">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-bold text-white">Email Editor</h3>
          
          {/* Visual/HTML Mode Toggle */}
          <div className="flex items-center gap-1 bg-white/20 backdrop-blur rounded-lg p-1">
            <button
              onClick={switchToVisualMode}
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition flex items-center gap-1.5 ${
                editorMode === 'visual'
                  ? 'bg-white text-blue-700 shadow-md'
                  : 'text-white hover:bg-white/10'
              }`}
              title="Visual Mode - Click to edit"
            >
              <Eye size={14} />
              👁️ Visual Mode
            </button>
            <button
              onClick={switchToHtmlMode}
              className={`px-4 py-1.5 rounded-md text-xs font-semibold transition flex items-center gap-1.5 ${
                editorMode === 'html'
                  ? 'bg-white text-purple-700 shadow-md'
                  : 'text-white hover:bg-white/10'
              }`}
              title="HTML Mode - Code editor"
            >
              <Code size={14} />
              &lt;/&gt; HTML Mode
            </button>
          </div>
        </div>

        {/* HTML Mode Controls */}
        {editorMode === 'html' && (
          <div className="flex items-center gap-2">
            {/* View Mode Toggles (for HTML mode) */}
            <div className="flex items-center gap-1 bg-white/20 backdrop-blur rounded-md p-1">
              <button
                onClick={() => setViewMode('code')}
                className={`px-3 py-1 rounded text-xs font-medium transition ${
                  viewMode === 'code'
                    ? 'bg-white text-gray-900'
                    : 'text-white hover:bg-white/10'
                }`}
                title="Code Only"
              >
                Code
              </button>
              <button
                onClick={() => setViewMode('split')}
                className={`px-3 py-1 rounded text-xs font-medium transition ${
                  viewMode === 'split'
                    ? 'bg-white text-gray-900'
                    : 'text-white hover:bg-white/10'
                }`}
                title="Split View"
              >
                Split
              </button>
              <button
                onClick={() => setViewMode('preview')}
                className={`px-3 py-1 rounded text-xs font-medium transition ${
                  viewMode === 'preview'
                    ? 'bg-white text-gray-900'
                    : 'text-white hover:bg-white/10'
                }`}
                title="Preview Only"
              >
                Preview
              </button>
            </div>

            {/* Refresh Preview */}
            <button
              onClick={refreshPreview}
              className="px-3 py-1 bg-white/20 backdrop-blur rounded-md text-xs font-medium text-white hover:bg-white/30 transition"
              title="Refresh Preview"
            >
              <RefreshCw size={14} className="inline mr-1" />
              Refresh
            </button>

            {/* Fullscreen Toggle */}
            <button
              onClick={toggleFullscreen}
              className="px-3 py-1 bg-white/20 backdrop-blur rounded-md text-xs font-medium text-white hover:bg-white/30 transition"
              title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              {isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
            </button>
          </div>
        )}
      </div>

      {/* Visual Mode */}
      {editorMode === 'visual' && (
        <div className="border-x border-b border-gray-300 rounded-b-lg p-4 bg-gray-50">
          <VisualEmailEditor
            value={html}
            onChange={handleVisualEditorChange}
            onInsertVariable={onInsertVariable}
          />
        </div>
      )}

      {/* HTML Mode */}
      {editorMode === 'html' && (
        <div className={`grid ${
          viewMode === 'split' ? 'grid-cols-2' : 'grid-cols-1'
        } border-x border-b border-gray-300 rounded-b-lg overflow-hidden`}>
          
          {/* Code Editor */}
          {(viewMode === 'code' || viewMode === 'split') && (
            <div className="bg-gray-900">
              <div className="bg-gray-800 px-3 py-2 border-b border-gray-700">
                <span className="text-xs font-mono text-gray-300">HTML Code</span>
              </div>
              <Editor
                height={isFullscreen ? 'calc(100vh - 100px)' : '600px'}
                defaultLanguage="html"
                value={html}
                onChange={handleEditorChange}
                onMount={handleEditorDidMount}
                theme="vs-dark"
                options={{
                  minimap: { enabled: true },
                  fontSize: 13,
                  lineNumbers: 'on',
                  roundedSelection: true,
                  scrollBeyondLastLine: false,
                  wordWrap: 'on',
                  automaticLayout: true,
                  tabSize: 2,
                  insertSpaces: true,
                  formatOnPaste: true,
                  formatOnType: true,
                  folding: true,
                  lineDecorationsWidth: 10,
                  lineNumbersMinChars: 4,
                  glyphMargin: false,
                  scrollbar: {
                    vertical: 'visible',
                    horizontal: 'visible',
                    useShadows: false,
                    verticalScrollbarSize: 10,
                    horizontalScrollbarSize: 10
                  }
                }}
              />
            </div>
          )}

          {/* Live Preview */}
          {(viewMode === 'preview' || viewMode === 'split') && (
            <div className={`bg-white ${viewMode === 'split' ? 'border-l border-gray-300' : ''}`}>
              <div className="bg-gray-100 px-3 py-2 border-b border-gray-300 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-700">Live Preview</span>
                <span className="text-xs text-gray-500">How your email will look</span>
              </div>
              <div 
                className="overflow-auto p-4 bg-gray-50"
                style={{ height: isFullscreen ? 'calc(100vh - 100px)' : '600px' }}
              >
                <div className="bg-white shadow-sm rounded border border-gray-200 p-4">
                  <iframe
                    ref={previewRef}
                    srcDoc={html}
                    className="w-full border-0"
                    style={{ 
                      minHeight: '500px',
                      height: 'auto'
                    }}
                    title="Email Preview"
                    sandbox="allow-same-origin"
                    onLoad={(e) => {
                      // Auto-resize iframe to content
                      const iframe = e.target;
                      if (iframe.contentWindow) {
                        try {
                          const height = iframe.contentWindow.document.body.scrollHeight;
                          iframe.style.height = `${height + 20}px`;
                        } catch (err) {
                          // Ignore cross-origin errors
                        }
                      }
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Help Text */}
      <div className="mt-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-xs text-blue-800">
          {editorMode === 'visual' ? (
            <>
              <strong>👁️ Visual Mode:</strong> Click any element in the preview to edit. 
              Blue outline = Headings, Green = Paragraphs, Orange = Images, Purple = Buttons. 
              Switch to HTML Mode for advanced editing.
            </>
          ) : (
            <>
              <strong>💡 HTML Mode:</strong> Edit your email HTML on the left. The preview on the right updates automatically. 
              Use variables like <code className="bg-blue-100 px-1 rounded">{'{{name}}'}</code>, <code className="bg-blue-100 px-1 rounded">{'{{email}}'}</code>, etc. 
              Switch to Visual Mode for easier editing.
            </>
          )}
        </p>
      </div>

      {/* Custom Styles */}
      <style>{`
        .rich-text-editor iframe {
          display: block;
        }
        
        /* Monaco Editor Customization */
        .monaco-editor .margin {
          background-color: #1e1e1e !important;
        }
        
        .monaco-editor .line-numbers {
          color: #858585 !important;
        }
      `}</style>
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;